import type { LegacyRef } from 'react';
import { forwardRef } from 'react';

import { WidgetProps } from '@highimpact-front/remote-widget-core';
import {
  ApiDashboardElement,
  ApiReturnCampaign,
} from '@highimpact-frontend/api-types';
import {
  DisplayerTypeEnum,
  isDisplayerTypeSuccess,
} from '@highimpact-frontend/displayer-types';
import {
  DisplayerErrorMessage,
  GenericErrorBoundary,
} from '@highimpact/dashboard-ui';

import { useDashboardLibrarySelector } from '../../../../runtime/DashboardLibraryProvider';
import { buildElementNavDisplayer } from '../../../../runtime/buildElementNavDisplayer';
import { isLegacyTableElement } from '../../../../runtime/elementMeasureSupport';
import { findGridIdAndTypeByElementId } from '../../../../state/store-operations';
import Widget from '../../commons/Widget';
import AggregationTable from '../aggregationTable/AggregationTable';
import TableByCampaign from '../tableByCampaign/TableByCampaign';
import LegacyTableReconfigurePlaceholder from '../tables/LegacyTableReconfigurePlaceholder';
import DisplayerNavComponent from './DisplayerNavComponent';
import { resolveRendererTypeFromElement } from './displayerElementConsistency';

interface ElementTableDisplayerProps extends WidgetProps {
  displayer_id: number;
  elementData: ApiDashboardElement;
  dashboardElementId: number;
  gridId: number;
  campaigns: ApiReturnCampaign[];
}

const nav_props = {
  [DisplayerTypeEnum.table]: {
    download: false,
    zoom: false,
    edit: true,
    delete: true,
    title: true,
  },
  [DisplayerTypeEnum.tablebycampaign]: {
    download: false,
    zoom: false,
    edit: true,
    delete: true,
    title: true,
  },
  DisplayerTypeError: { delete: true, title: true },
};

function ElementTableDisplayerWidget(
  {
    displayer_id,
    is_editable,
    dashboardOnlyMode,
    non_removable,
    elementData,
    widget_id,
    gridId,
    site,
    campaigns,
    ...props
  }: ElementTableDisplayerProps,
  ref: LegacyRef<HTMLDivElement>
) {
  const { dashboardElementId } = props;
  const navDisplayer = buildElementNavDisplayer(elementData, displayer_id);
  const rendererType = resolveRendererTypeFromElement(elementData);

  const gridData = useDashboardLibrarySelector((state) =>
    findGridIdAndTypeByElementId(dashboardElementId, state.dashboard)
  );
  const gridType = gridData?.type;

  const tableSubProps = {
    dashboardElementId,
    is_editable,
    elementData,
    non_removable,
    gridId,
    widget_id,
    site,
    campaigns,
  };

  const tableContent = (() => {
    if (rendererType === 'DisplayerTypeError') {
      return <DisplayerErrorMessage name={elementData.name} />;
    }

    if (isLegacyTableElement(elementData)) {
      return (
        <div className="widget-content">
          <LegacyTableReconfigurePlaceholder
            element={elementData}
            gridId={gridId}
            gridType={gridType ?? 'grids'}
            dashboardElementId={dashboardElementId}
          />
        </div>
      );
    }

    return (
      <GenericErrorBoundary
        fallback={<DisplayerErrorMessage name={"Impossible d'afficher"} />}
      >
        {
          {
            [DisplayerTypeEnum.table]: <AggregationTable {...tableSubProps} />,
            [DisplayerTypeEnum.tablebycampaign]: (
              <TableByCampaign {...tableSubProps} />
            ),
          }[
            rendererType as
              | DisplayerTypeEnum.table
              | DisplayerTypeEnum.tablebycampaign
          ]
        }
      </GenericErrorBoundary>
    );
  })();

  return (
    <Widget
      ref={ref}
      is_editable={is_editable}
      non_removable={non_removable}
      widget_id={widget_id}
      dashboardOnlyMode={dashboardOnlyMode}
      site={site}
      {...props}
      className={`displayer ${props.className || ''}`}
    >
      {isDisplayerTypeSuccess(navDisplayer) && (
        <DisplayerNavComponent
          {...tableSubProps}
          displayer={navDisplayer}
          dashboardOnlyMode={dashboardOnlyMode}
          buttons={
            dashboardOnlyMode
              ? {}
              : {
                  ...(nav_props[rendererType as keyof typeof nav_props] ??
                    nav_props.DisplayerTypeError),
                  delete: !non_removable,
                }
          }
          gridId={gridId}
          gridType={gridType!}
        />
      )}

      {tableContent}
    </Widget>
  );
}

export default forwardRef(ElementTableDisplayerWidget);
